/* =========================================================
   Recharge Bundle Revamp (RBX) — Final Complete JS
   ✅ All original functionality preserved
   ✅ Placeholder disabled ATC button always visible in slot
   ✅ FIX: Double colon :: removed from delivery selector
   ✅ FIX: Steps/tabs hidden class + defensive fallback logging
   ✅ FIX: No scroll on initial load when mode is set
   ✅ FIX: No scroll when changing delivery frequency dropdown
   ✅ DYNAMIC: Steps freely switchable regardless of selection
   ✅ DYNAMIC: ATC remains disabled until all sections complete
   ✅ DYNAMIC: Sticky chips + count match actual number of steps
   ✅ DYNAMIC: Works across any bundle product with N steps
   ✅ FIX: Over-selection loopholes closed — users cannot exceed bundle limit
========================================================= */

(() => {
  const ROOT = document.querySelector("#recharge-bundles");
  if (!ROOT) return;

  const qs  = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));

  // ---------- Module-level sections reference ----------
  // ✅ FIX: Stored once widgets are ready so cap checks are
  //    available inside renderPurchaseUI's setMode closure
  //    without requiring a full call-signature refactor.
  let _sections = [];

  // ---------- Config ----------
  function getCFG() {
    const el = qs("[data-rbx-json]");
    if (!el) return null;
    try {
      return JSON.parse(
        (el.textContent || "").trim().replace(/"img"\s*:\s*Liquid error[^,}\]]*/g, '"img":""')
      );
    } catch (e) {
      console.error("[RBX] bad JSON", e);
      return null;
    }
  }

  // ---------- Widget ready ----------
  function waitForWidget(cb) {
    let tries = 0;
    const t = setInterval(() => {
      const boxBuilder = qs(".rb-box-builder", ROOT);
      const sections   = qsa(".rb-section", ROOT);
      const select     = qs("#selling-plan-list", ROOT);
      if (boxBuilder && sections.length && select) {
        clearInterval(t);
        console.log("[RBX] Widget ready — sections found:", sections.length);
        cb({ boxBuilder, sections, select });
      }
      if (++tries > 160) {
        clearInterval(t);
        console.warn("[RBX] waitForWidget timed out. boxBuilder:", !!qs(".rb-box-builder", ROOT), "sections:", qsa(".rb-section", ROOT).length, "select:", !!qs("#selling-plan-list", ROOT));
      }
    }, 250);
  }

  // ---------- Helpers ----------
  const safeHTML = (s) => { const d = document.createElement("div"); d.textContent = s || ""; return d.innerHTML; };

  const sectionTitle  = (s) => qs(".rb-section-title", s)?.textContent?.trim() || "Step";
  const sectionNeed   = (s) => parseInt(qs(".rb-remainder", s)?.textContent?.match(/Choose\s+(\d+)/i)?.[1] || "0", 10);
  const sectionChosen = (s) => qsa("input.rb-input", s).reduce((n, i) => { const v = parseInt((i.value || "0").replace(/\D/g, ""), 10); return n + (isNaN(v) ? 0 : v); }, 0);
  const isSectionComplete = (s) => { const n = sectionNeed(s); return !n || sectionChosen(s) >= n; };

  // ✅ FIX: Over-selection guards
  // isSectionOver  — true when a section has MORE items than its required quota
  // isAnyOver      — true when ANY section in the bundle is over its quota
  const isSectionOver = (s) => { const n = sectionNeed(s); return n > 0 && sectionChosen(s) > n; };
  const isAnyOver     = (secs) => (secs || _sections).some((s) => isSectionOver(s));

  function parsePrices() {
    const compareText = qs(".rb-full-price", ROOT)?.textContent.replace(/\s+/g, " ").trim() || "";
    const wrap = qs(".rb-price", ROOT);
    const txt  = wrap?.textContent.replace(/\s+/g, " ").trim() || "";
    const parts = txt.split(" ").filter(Boolean);
    return { compareText, currentText: parts[parts.length - 1] || txt };
  }

  function simpleCurrency(s) {
    if (!s) return "";
    return String(s).trim()
      .replace(/[A-Z]{2,3}\s*\$/gi, "$")
      .replace(/\$\s*[A-Z]{2,3}/gi, "$")
      .replace(/[A-Za-z]/g, "")
      .replace(/\s+/g, "");
  }

  // ✅ FIX: Strip all leading "Delivery" variants before building label to prevent "::"
  function formatDeliveryLabel(raw, prefix = "Delivery:") {
    const stripped = (raw || "")
      .replace(/^delivery\s*:?\s*/i, "")
      .replace(/subscription\s+with.*$/i, "")
      .replace(/with.*$/i, "")
      .trim();

    const cleaned = stripped.replace(/^[:\s]+/, "").trim();

    const m = cleaned.match(/(\d+)\s*(weeks?|months?)/i);
    let body;
    if (m) {
      const num  = parseInt(m[1], 10);
      const unit = m[2].toLowerCase().replace(/s$/, "");
      body = `${num} ${unit}${num !== 1 ? "s" : ""}`;
    } else {
      body = cleaned || stripped || raw || "";
    }

    const safePrefix = (prefix || "Delivery:").replace(/[:\s]+$/, ":");
    return `${safePrefix} ${body}`;
  }

  // ---------- Calculate discount badge label ----------
  function calcBadgeLabel(CFG) {
    const { compareText, currentText } = parsePrices();
    const full = parseFloat((compareText || "").replace(/[^0-9.]/g, ""));
    const sale = parseFloat((currentText || "").replace(/[^0-9.]/g, ""));
    if (full > 0 && sale > 0 && sale < full) {
      const pct = Math.round((1 - sale / full) * 100);
      if (pct > 0 && pct < 100) return `Save ${pct}%`;
    }
    return (CFG?.copy?.badgeLabel || "Save 30%").trim();
  }

  // ---------- Apply CSS vars ----------
  function setVars(CFG) {
    const st = CFG?.style || {};
    const pairs = [
      ["--rbx-bg", st.bg], ["--rbx-text", st.text], ["--rbx-muted", st.muted],
      ["--rbx-border", st.border], ["--rbx-pill-bg", st.pillBg], ["--rbx-pill-text", st.pillText],
      ["--rbx-badge-bg", st.badgeBg], ["--rbx-badge-text", st.badgeText],
      ["--rbx-cta-bg", st.ctaBg], ["--rbx-cta-text", st.ctaText],
    ];
    pairs.forEach(([k, v]) => v != null && v !== "" && ROOT.style.setProperty(k, v));
    if (st.radius != null && String(st.radius).trim()) {
      ROOT.style.setProperty("--rbx-radius", typeof st.radius === "number" ? `${st.radius}px` : st.radius);
    }
  }

  // ---------- Gifts ----------
  function buildGiftRows(CFG) {
    return (CFG.gifts || [])
      .filter((g) => (g.text || "").trim())
      .map((g) => {
        const img = (g.img || "").trim()
          ? `<img class="rbx-gift-img" src="${g.img.trim()}" alt="">`
          : `<div class="rbx-gift-img"></div>`;
        return `
          <div class="rbx-gift">
            <div class="rbx-gift-left">
              ${img}
              <div>
                <div class="rbx-gift-month">${safeHTML(g.month || "")}</div>
                <div class="rbx-gift-text">${safeHTML(g.text || "")}</div>
              </div>
            </div>
            <div class="rbx-gift-right">
              ${g.was ? `<div class="rbx-gift-was">${safeHTML(g.was)}</div>` : ""}
              <div class="rbx-gift-free">${safeHTML(g.right || "")}</div>
            </div>
          </div>`;
      })
      .join("");
  }

  // ---------- Section header / counters ----------
  function getSectionHeaderEl(sec) {
    return sec.querySelector(".tflex.tflex-wrap.titems-baseline.tjustify-between")
      || qs(".rb-section-header", sec)
      || qs(".rb-section-title-row", sec)
      || qs(".rb-section-title", sec)?.parentElement
      || qs(".rb-section-title", sec)
      || null;
  }

  function prefixChooseToSectionTitles(sections) {
    sections.forEach((sec) => {
      const el = qs(".rb-section-title", sec);
      if (!el || el.dataset.rbxPrefixed) return;
      const txt = el.textContent.trim();
      el.textContent = /^choose\b/i.test(txt) ? txt : `Choose ${txt}`;
      el.dataset.rbxPrefixed = "true";
    });
  }

  function ensureSectionCounters(sections) {
    sections.forEach((sec) => {
      if (!sectionNeed(sec)) return;
      const header = getSectionHeaderEl(sec);
      if (!header || qs(".rbx-sec-counter", header)) return;
      const c = document.createElement("div");
      c.className = "rbx-sec-counter";
      c.setAttribute("aria-live", "polite");
      header.appendChild(c);
    });
  }

  function updateSectionCounters(sections) {
    sections.forEach((sec) => {
      const need = sectionNeed(sec);
      if (!need) return;
      const header = getSectionHeaderEl(sec);
      const counter = header && qs(".rbx-sec-counter", header);
      if (counter) {
        const chosen = sectionChosen(sec);
        // ✅ FIX: Show warning state in counter when section is over limit
        counter.textContent = `(${chosen}/${need})`;
        counter.classList.toggle("rbx-sec-counter--over", chosen > need);
      }
    });
  }

  // ---------- Steps (tabs) ----------
  // ✅ DYNAMIC: Steps are always freely navigable — no completion gate
  function buildStepsUI(root, boxBuilder, sections) {
    qs(".rbx-steps", root)?.remove();

    const bar = document.createElement("div");
    bar.className = "rbx-steps";
    bar.setAttribute("data-rbx-steps", "true");

    const buttons = sections.map((sec, idx) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "rbx-step";
      b.dataset.step = idx;
      b.textContent = `STEP ${String(idx + 1).padStart(2, "0")}`;
      b.setAttribute("aria-label", sectionTitle(sec));
      bar.appendChild(b);
      return b;
    });

    boxBuilder.parentElement.insertBefore(bar, boxBuilder);

    const setActiveStep = (i) => {
      sections.forEach((s, idx) => {
        const hidden = idx !== i;
        s.classList.toggle("rbx-section-hidden", hidden);
        s.style.display = hidden ? "none" : "";
      });
      buttons.forEach((b, idx) => b.classList.toggle("is-active", idx === i));
      console.log("[RBX] Active step set to:", i);
    };

    // ✅ All step buttons are always enabled — user can freely navigate
    const refreshDisabled = () => {
      buttons.forEach((b) => {
        b.disabled = false;
        b.classList.remove("is-disabled");
      });
    };

    buttons.forEach((b) => {
      b.addEventListener("click", () => {
        if (!b.disabled) setActiveStep(+b.dataset.step);
      });
    });

    setActiveStep(0);
    refreshDisabled();
    return { setActiveStep, refreshDisabled };
  }

  const getActiveMode = () =>
    (qs('#rbx-purchase-box input[name="rbx_mode"]:checked', ROOT)?.value === "sub") ? "sub" : "one";

  // ---------- Find the real hidden Recharge ATC button ----------
  function getRealBtn() {
    return [
      qs(".rb-sticky-form .rb-btn-wrapper > button.rb-btn", ROOT),
      qs(".status-bar-sign-up .rb-form > button.rb-btn", ROOT),
    ].filter(Boolean)[0] || null;
  }

  function isRealBtnReady() {
    const b = getRealBtn();
    if (!b) return false;
    return !b.disabled
      && b.getAttribute("aria-disabled") !== "true"
      && !b.classList.contains("disabled");
  }

  // ---------- Proxy button ----------
  function injectPlaceholderBtn(CFG) {
    const slot = qs("#rbx-cta-slot", ROOT);
    if (!slot || document.getElementById("rbx-proxy-btn")) return;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "rbx-proxy-btn";
    btn.className = "rb-btn rbx-cta-disabled";
    btn.textContent = (CFG?.copy?.ctaDisabledText || "ADD TO CART").trim();

    const fireReal = () => {
      const real = getRealBtn();
      if (!real) return;
      real.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    };

    // ✅ FIX: Only fire if the proxy is genuinely enabled.
    //    CSS classes alone don't block clicks — this is the authoritative gate.
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      if (!btn.classList.contains("rbx-cta-enabled")) return;
      fireReal();
    });

    slot.appendChild(btn);
  }

  function hideRealBtn() {
    const real = getRealBtn();
    if (real && !real.dataset.rbxHidden) {
      real.style.cssText += ";position:absolute!important;opacity:0!important;pointer-events:none!important;width:1px!important;height:1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;";
      real.dataset.rbxHidden = "true";
    }
  }

  // ---------- CTA presentation ----------
  // ✅ FIX: Added isAnyOver(_sections) as a second gate.
  //    Uses the module-level _sections reference so this function
  //    doesn't need a signature change — all existing call sites work.
  function syncCtaPresentation(CFG) {
    const box   = qs("#rbx-purchase-box", ROOT);
    const proxy = document.getElementById("rbx-proxy-btn");
    if (!proxy) return;

    hideRealBtn();

    // ✅ FIX: Cache once — used twice below (enabled check + button label)
    const over    = isAnyOver();
    // ✅ FIX: Recharge ready AND no section is over its limit
    const enabled = isRealBtnReady() && !over;

    qs(".rbx-card", box)?.setAttribute("data-cta-enabled", enabled ? "true" : "false");
    const contains = qs(".rbx-contains", box);
    if (contains) contains.style.display = enabled ? "" : "none";

    if (!enabled) {
      proxy.classList.remove("rbx-cta-enabled");
      proxy.classList.add("rbx-cta-disabled");
      proxy.removeAttribute("data-enabled");
      // ✅ FIX: Distinct message when over limit vs under limit — uses cached `over`
      const overText  = (CFG?.copy?.ctaOverText || CFG?.copy?.ctaDisabledText || "ADD TO CART").trim();
      const underText = (CFG?.copy?.ctaDisabledText || "ADD TO CART").trim();
      proxy.textContent = over ? overText : underText;
      return;
    }

    proxy.classList.remove("rbx-cta-disabled");
    proxy.classList.add("rbx-cta-enabled");
    proxy.dataset.enabled = "true";

    const { compareText, currentText } = parsePrices();
    const raw   = currentText || compareText;
    const price = simpleCurrency(raw.trim());
    const label = (CFG?.copy?.ctaText || "ADD TO CART").trim();
    proxy.textContent = price ? `${price} | ${label}` : label;
  }

  function mountAddBtnIntoSlot(CFG) {
    syncCtaPresentation(CFG);
  }

  // ---------- Serving labels ----------
  function enhanceServingLabels(sections) {
    sections.forEach((sec, secIdx) => {
      if (secIdx >= 2) return;
      qsa(".rb-item", sec).forEach((item) => {
        if (item.dataset.rbxServings) return;
        const titleEl = qs(".rb-title", item);
        if (!titleEl) return;
        const name = (titleEl.textContent || "").toLowerCase();
        const count = name.includes("sachet") ? "30" : "12";
        const label = document.createElement("div");
        label.className = "rbx-serving-label";
        label.textContent = `${count} Servings`;
        titleEl.after(label);
        item.dataset.rbxServings = count;
      });
    });
  }

  // ---------- Qty Enhancement ----------
  // ✅ FIX: + ADD button now checks section capacity before delegating
  //    to Recharge's native + button. Also hides + ADD when section
  //    is at or over its limit so the UI reflects the constraint.
  function enhanceQtyWrappers() {
    qsa("#recharge-bundles .rb-qty-wrapper").forEach((w) => {
      if (w.classList.contains("rbx-qty-enhanced")) return;
      const plus  = qs(".rb-btn-qty-plus", w);
      const minus = qs(".rb-btn-qty-minus", w);
      const input = qs("input.rb-input", w);
      if (!plus || !minus || !input) return;

      w.classList.add("rbx-qty-enhanced");

      const add = document.createElement("button");
      add.type = "button";
      add.className = "rbx-temp-add";
      add.textContent = "+ ADD";
      add.setAttribute("aria-label", "Add item");
      w.appendChild(add);

      const getQty = () => { const v = parseInt(String(input.value || "0").replace(/\D/g, ""), 10); return isNaN(v) ? 0 : v; };

      // ✅ FIX: setState also hides + ADD when the section is at/over capacity
      const setState = () => {
        const qty = getQty();
        w.classList.toggle("rbx-has-qty", qty > 0);

        const sec  = w.closest(".rb-section");
        const need = sec ? sectionNeed(sec) : 0;
        const chosen = sec ? sectionChosen(sec) : 0;
        // Hide + ADD when the section has already met its quota
        if (need > 0) {
          add.style.display = chosen >= need ? "none" : "";
        }
      };

      // ✅ FIX: Block click entirely when section is already at/over limit
      add.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sec    = w.closest(".rb-section");
        const need   = sec ? sectionNeed(sec) : 0;
        const chosen = sec ? sectionChosen(sec) : 0;
        if (need > 0 && chosen >= need) return; // hard cap — do nothing
        plus.click();
        setTimeout(setState, 50);
      });

      [input].forEach((el) => el.addEventListener("input", () => setTimeout(setState, 0)));
      [plus, minus].forEach((el) => el.addEventListener("click", () => setTimeout(setState, 50)));

      setState();
    });
  }

  // ---------- Purchase UI ----------
  function renderPurchaseUI(CFG, selectEl) {
    qs("#rbx-purchase-box", ROOT)?.remove();

    const opts = qsa("option", selectEl).map((o) => ({
      id:   o.getAttribute("data-selling-plan-id") || "0",
      text: o.textContent.replace(/\s+/g, " ").trim(),
    }));

    const oneTime    = opts.find((o) => o.id === "0") || opts[0];
    const subs       = opts.filter((o) => o.id !== "0");
    const selectedId = qs("option:checked", selectEl)?.getAttribute("data-selling-plan-id") || oneTime?.id || "0";
    const initialMode = selectedId !== "0" ? "sub" : "one";
    const prices = parsePrices();

    const otpOptionEl  = qsa("option", selectEl).find((o) => (o.getAttribute("data-selling-plan-id") || "0") === "0");
    const rawOtpFull   = otpOptionEl?.getAttribute("data-compare-price")
                      || otpOptionEl?.getAttribute("data-full-price")
                      || otpOptionEl?.getAttribute("data-original-price")
                      || prices.compareText || "";
    const rawOtpSale   = otpOptionEl?.getAttribute("data-price")
                      || otpOptionEl?.getAttribute("data-current-price")
                      || otpOptionEl?.getAttribute("data-sale-price")
                      || prices.compareText || "";
    const otpCompare   = simpleCurrency(rawOtpFull);
    const otpCurrent   = simpleCurrency(rawOtpSale);

    const badge = calcBadgeLabel(CFG);
    const summaryTitle  = (CFG?.copy?.summaryTitle || CFG?.copy?.summary_title || "Your Bundle contains:").trim();
    const summaryPoints = [1, 2, 3]
      .map((n) => (CFG?.copy?.[`summaryPoint${n}`] || "").trim())
      .filter(Boolean);

    const rawDeliveryPrefix = (CFG?.copy?.deliveryPrefix || "Delivery:").trim();
    const deliveryPrefix    = rawDeliveryPrefix.replace(/[:\s]+$/, "") + ":";

    const box = document.createElement("div");
    box.id = "rbx-purchase-box";
    box.className = "rb-container";

    box.innerHTML = `
      <div class="rbx-card" data-cta-enabled="false">
        <div class="rbx-title">${safeHTML(CFG.copy.title || "Choose your buying frequency:")}</div>

        <!-- SUBSCRIBE FIRST -->
        <div class="rbx-option ${initialMode === "sub" ? "is-active" : ""}" data-mode="sub" role="radio"
             aria-checked="${initialMode === "sub"}" tabindex="${initialMode === "sub" ? 0 : -1}">
          <div class="rbx-row">
            <div class="rbx-left">
              <input class="rbx-radio" type="radio" name="rbx_mode" value="sub" ${initialMode === "sub" ? "checked" : ""}>
              <div class="rbx-label">
                ${safeHTML(CFG.copy.subscribeLabel || "Subscribe")}
                <span class="rbx-badge">${safeHTML(badge)}</span>
              </div>
            </div>
            <div class="rbx-right">
              ${prices.compareText ? `<div class="rbx-compare">${safeHTML(simpleCurrency(prices.compareText))}</div>` : ""}
              <div class="rbx-price">${safeHTML(simpleCurrency(prices.currentText || ""))}</div>
            </div>
          </div>
          <div class="rbx-delivery" data-rbx-delivery style="${initialMode === "sub" ? "" : "display:none"}">
            <div class="rbx-pill" data-rbx-pill>
              <div class="rbx-pill-label" data-rbx-pill-label></div>
              <select aria-label="${safeHTML(deliveryPrefix)}" data-rbx-delivery-select class="rbx-pill-select">
                ${subs.map((o) => {
                  const label = formatDeliveryLabel(o.text, deliveryPrefix);
                  return `<option value="${o.id}" ${o.id === selectedId ? "selected" : ""}>${safeHTML(label)}</option>`;
                }).join("")}
              </select>
              <div class="rbx-pill-right">
                <span class="rbx-badge">${safeHTML(badge)}</span>
                <span class="rbx-chevron" aria-hidden="true">
                  <svg width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.5 0.5L6.5 6.5L12.5 0.5" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>
            <div class="rbx-benefits">
              <div class="rbx-benefits__title">${safeHTML(CFG.copy.benefitsTitle || "Subscription includes:")}</div>
              ${buildGiftRows(CFG)}
            </div>
          </div>
          <div class="rbx-cancel">${safeHTML(CFG.copy.cancelNote || "Cancel anytime — no commitment required.")}</div>
        </div>

        <!-- ONE-TIME SECOND -->
        <div class="rbx-option ${initialMode === "one" ? "is-active" : ""}" data-mode="one" role="radio"
             aria-checked="${initialMode === "one"}" tabindex="${initialMode === "one" ? 0 : -1}">
          <div class="rbx-row">
            <div class="rbx-left">
              <input class="rbx-radio" type="radio" name="rbx_mode" value="one" ${initialMode === "one" ? "checked" : ""}>
              <div class="rbx-label">${safeHTML(CFG.copy.oneTimeLabel || "One-time")}</div>
            </div>
            <div class="rbx-right">
              ${otpCompare && otpCompare !== otpCurrent ? `<div class="rbx-compare">${safeHTML(otpCompare)}</div>` : ""}
              <div class="rbx-price">${safeHTML(otpCurrent)}</div>
            </div>
          </div>
        </div>

        <div class="rbx-contains">
          <div class="rbx-contains-title">${safeHTML(summaryTitle)}</div>
          <div class="rbx-contains-line">
            ${summaryPoints.map((p, i) =>
              `<span class="rbx-contains-item">${safeHTML(p)}</span>${i < summaryPoints.length - 1 ? `<span class="rbx-contains-sep">|</span>` : ""}`
            ).join("")}
          </div>
        </div>

        <div class="rbx-cta-slot" id="rbx-cta-slot"></div>
      </div>`;

    const lastSection = qsa(".rb-section", ROOT).at(-1);
    (lastSection
      ? (el) => lastSection.after(el)
      : (el) => (qs(".rb-box-builder", ROOT) || ROOT).appendChild(el)
    )(box);

    setTimeout(() => {
      const prevSelected = qs("option:checked", selectEl);
      const otpOpt = qsa("option", selectEl).find(o => (o.getAttribute("data-selling-plan-id") || "0") === (oneTime?.id || "0"));
      if (!otpOpt || otpOpt === prevSelected) return;

      otpOpt.selected = true;
      selectEl.dispatchEvent(new Event("change", { bubbles: true }));

      setTimeout(() => {
        const p = parsePrices();
        const patchCompare = simpleCurrency(p.compareText);
        const patchCurrent = simpleCurrency(p.currentText || p.compareText);

        const otpBox = qs('.rbx-option[data-mode="one"]', box);
        if (otpBox) {
          const right = qs(".rbx-right", otpBox);
          if (right) {
            right.innerHTML = `
              ${patchCompare && patchCompare !== patchCurrent ? `<div class="rbx-compare">${safeHTML(patchCompare)}</div>` : ""}
              <div class="rbx-price">${safeHTML(patchCurrent)}</div>`;
          }
        }

        if (prevSelected) prevSelected.selected = true;
        selectEl.dispatchEvent(new Event("change", { bubbles: true }));
      }, 150);
    }, 100);

    const deliverySel = qs("[data-rbx-delivery-select]", box);
    const pillLabel   = qs("[data-rbx-pill-label]", box);
    const card        = qs(".rbx-card", box);

    const syncPillLabel = () => {
      if (!deliverySel || !pillLabel) return;
      pillLabel.textContent = deliverySel.options[deliverySel.selectedIndex]?.textContent.trim() || "";
    };

    const setRechargePlan = (planId) => {
      const opt = qsa("option", selectEl).find((x) => (x.getAttribute("data-selling-plan-id") || "0") === String(planId));
      if (!opt) return;
      opt.selected = true;
      selectEl.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const syncAria = (activeMode) => {
      card?.setAttribute("role", "radiogroup");
      qsa(".rbx-option", box).forEach((opt) => {
        const active = opt.dataset.mode === activeMode;
        opt.classList.toggle("is-active", active);
        opt.setAttribute("aria-checked", active);
        opt.setAttribute("tabindex", active ? "0" : "-1");
      });
    };

    const syncCancelVisibility = (m) => {
      const cancel = qs(".rbx-cancel", box);
      if (cancel) cancel.style.display = m === "one" ? "none" : "";
    };

    // ✅ FIX: isInitialLoad flag prevents scrollIntoView firing on page load
    let isInitialLoad = true;

    const setMode = (m, noScroll = false) => {
      const delivery = qs("[data-rbx-delivery]", box);
      if (m === "one") {
        if (delivery) delivery.style.display = "none";
        setRechargePlan(oneTime?.id || "0");
      } else {
        if (delivery) delivery.style.display = "";
        const chosen = deliverySel?.value || subs[0]?.id;
        if (chosen) setRechargePlan(chosen);
      }
      syncAria(m);
      syncCancelVisibility(m);
      // ✅ syncCtaPresentation uses _sections internally — no change needed here
      syncCtaPresentation(CFG);

      // ✅ Only scroll if user triggered a mode switch — never on initial load or dropdown change
      if (!isInitialLoad && !noScroll) {
        requestAnimationFrame(() => {
          const purchaseBox = document.getElementById("rbx-purchase-box");
          if (!purchaseBox) return;
          const rect = purchaseBox.getBoundingClientRect();
          const viewH = window.innerHeight || document.documentElement.clientHeight;
          if (rect.bottom > viewH || rect.top < 0) {
            try {
              purchaseBox.scrollIntoView({ behavior: "smooth", block: "end" });
            } catch (_) {
              purchaseBox.scrollIntoView(false);
            }
          }
        });
      }
    };

    syncPillLabel();
    setMode(initialMode);
    isInitialLoad = false; // ✅ Release scroll lock after initial setup

    qsa('input[name="rbx_mode"]', box).forEach((r) => r.addEventListener("change", () => setMode(r.value)));

    qsa(".rbx-option", box).forEach((opt) => {
      const activate = () => {
        const r = qs(`input[name="rbx_mode"][value="${opt.dataset.mode}"]`, box);
        if (!r) return;
        r.checked = true;
        r.dispatchEvent(new Event("change", { bubbles: true }));
      };
      opt.addEventListener("click",   (e) => { if (!e.target?.closest(".rbx-radio")) activate(); });
      opt.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } });
    });

    if (deliverySel) {
      deliverySel.addEventListener("change", () => {
        qs('input[name="rbx_mode"][value="sub"]', box).checked = true;
        syncPillLabel();
        setMode("sub", true); // ✅ suppress scroll — user is already looking at the dropdown
        setTimeout(() => { try { deliverySel.blur(); } catch (_) {} }, 0);
      });
      deliverySel.addEventListener("keyup", (e) => {
        if (e.key === "Escape") { try { deliverySel.blur(); } catch (_) {} }
      });
    }
  }

  // =========================================================
  // CUSTOM STICKY BAR
  // =========================================================

  function isElementInView(el, threshold = 0.35) {
    if (!el) return false;
    const r  = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return (Math.min(r.bottom, vh) - Math.max(r.top, 0)) / Math.max(r.height, 1) >= threshold;
  }

  const requiredSections      = (sections) => { const r = sections.filter((s) => sectionNeed(s) > 0); return r.length ? r : sections; };
  const isAllRequiredComplete = (sections) => requiredSections(sections).every((s) => sectionChosen(s) >= sectionNeed(s));

  // ✅ DYNAMIC: total reflects actual required sections — no artificial cap
  function getStageProgress(sections) {
    const req   = requiredSections(sections);
    const total = req.length || sections.length;
    const done  = Math.min(
      req.filter((s) => sectionChosen(s) >= sectionNeed(s)).length,
      total
    );
    return { done, total };
  }

  function getPlans(sel) {
    const opts = qsa("option", sel).map((o) => ({ id: o.getAttribute("data-selling-plan-id") || "0", text: (o.textContent || "").trim() }));
    return { oneTime: opts.find((o) => o.id === "0") || opts[0], subs: opts.filter((o) => o.id !== "0") };
  }

  function setRechargePlanGlobal(sel, planId) {
    const opt = qsa("option", sel).find((x) => (x.getAttribute("data-selling-plan-id") || "0") === String(planId));
    if (!opt) return;
    opt.selected = true;
    sel.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // ✅ DYNAMIC: receives sections to build the correct number of chips
  function ensureStickyBar(CFG, sections) {
    if (document.getElementById("rbx-sticky")) return;

    const badge = (CFG?.copy?.badgeLabel || "Save 30%").trim();

    // ✅ DYNAMIC: derive chip count and labels directly from actual sections
    const req        = requiredSections(sections);
    const stepCount  = req.length || sections.length;
    const chipLabels = req.length
      ? req.map((sec) => sectionTitle(sec))
      : sections.map((sec) => sectionTitle(sec));

    const sticky = document.createElement("div");
    sticky.id = "rbx-sticky";
    sticky.innerHTML = `
      <div class="rbx-sticky-inner">
        <div class="rbx-sticky-content">
          <div class="rbx-sticky-left">
            <div class="rbx-sticky-top">
              <div class="rbx-sticky-title" data-rbx-sticky-title>Selected 0/${stepCount}</div>
              <div class="rbx-sticky-chips" data-rbx-sticky-chips>
                ${chipLabels.map((label, i) =>
                  `<div class="rbx-chip" data-chip="${i}"><span class="rbx-dot">✓</span><span>${safeHTML(label)}</span></div>`
                ).join("")}
              </div>
            </div>
            <div class="rbx-sticky-priceRow">
              <span class="rbx-sticky-compare" data-rbx-sticky-compare></span>
              <span class="rbx-sticky-price"   data-rbx-sticky-price></span>
              <span class="rbx-sticky-badge"   data-rbx-sticky-badge>${safeHTML(badge)}</span>
            </div>
          </div>
          <div class="rbx-sticky-actions">
            <button type="button" class="rbx-btn2" data-rbx-atc-one disabled>ADD TO CART</button>
            <button type="button" class="rbx-btn2 rbx-btn2--sub" data-rbx-atc-sub disabled>
              SUBSCRIBE &amp; SAVE<small>+ Get Exclusive Gifts</small>
            </button>
          </div>
        </div>
      </div>`;

    document.body.appendChild(sticky);

    const fireAtc = () => {
      const real = getRealBtn();
      if (!real) return;
      real.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
    };

    const atcOne = sticky.querySelector("[data-rbx-atc-one]");
    const atcSub = sticky.querySelector("[data-rbx-atc-sub]");

    atcOne.addEventListener("click", function (e) {
      e.preventDefault();
      if (this.disabled) return;
      const sel = qs("#selling-plan-list", ROOT);
      if (!sel) return;
      setRechargePlanGlobal(sel, getPlans(sel).oneTime?.id || "0");
      fireAtc();
    });

    atcSub.addEventListener("click", function (e) {
      e.preventDefault();
      if (this.disabled) return;
      const sel = qs("#selling-plan-list", ROOT);
      if (!sel) return;
      const { subs } = getPlans(sel);
      const current  = qs("option:checked", sel)?.getAttribute("data-selling-plan-id") || "0";
      const chosen   = current !== "0" ? current : (subs[0]?.id || "0");
      if (chosen && chosen !== "0") setRechargePlanGlobal(sel, chosen);
      fireAtc();
    });
  }

  function syncStickyBar(CFG, sections) {
    const sticky = document.getElementById("rbx-sticky");
    if (!sticky) return;

    sticky.classList.toggle("is-hidden", isElementInView(qs("#rbx-purchase-box", ROOT), 0.35));

    const { done, total } = getStageProgress(sections);

    // ✅ FIX: ready requires BOTH minimum met AND no section is over its limit
    const ready = isAllRequiredComplete(sections) && !isAnyOver(sections);

    // ✅ DYNAMIC: title reflects the real total from this bundle's sections
    sticky.querySelector("[data-rbx-sticky-title]").textContent = `Selected ${done}/${total}`;

    // Sync each chip — complete = green, over = warning, incomplete = default
    const req = requiredSections(sections);
    qsa("[data-chip]", sticky).forEach((c) => {
      const idx = parseInt(c.dataset.chip, 10);
      const sec = req[idx];
      const complete = sec ? isSectionComplete(sec) : false;
      const over     = sec ? isSectionOver(sec)     : false;
      c.classList.toggle("is-on",   complete && !over);
      // ✅ FIX: Add a visible warning state on the chip when over limit
      c.classList.toggle("is-over", over);
    });

    // ✅ FIX: ATC disabled when over limit — even if minimum is met
    sticky.querySelector("[data-rbx-atc-one]").disabled = !ready;
    sticky.querySelector("[data-rbx-atc-sub]").disabled = !ready;

    const { compareText, currentText } = parsePrices();
    const mode  = getActiveMode();
    const isSub = mode === "sub";
    const displayPrice   = currentText || compareText;
    const displayCompare = compareText || "";

    sticky.querySelector("[data-rbx-sticky-compare]").textContent = simpleCurrency(displayCompare) || "";
    sticky.querySelector("[data-rbx-sticky-price]").textContent   = simpleCurrency((displayPrice || "").trim()) || "";

    const badgeEl = sticky.querySelector("[data-rbx-sticky-badge]");
    badgeEl.textContent   = isSub ? calcBadgeLabel(CFG) : "";
    badgeEl.style.display = isSub ? "" : "none";
  }

  // ---------- Boot ----------
  function boot() {
    const CFG = getCFG();
    if (!CFG || CFG.enabled === false) return;

    setVars(CFG);

    waitForWidget(({ boxBuilder, sections, select }) => {
      // ✅ FIX: Store sections in module scope so syncCtaPresentation
      //    and isAnyOver() can reference them without signature changes
      _sections = sections;

      const steps = buildStepsUI(ROOT, boxBuilder, sections);
      renderPurchaseUI(CFG, select);

      injectPlaceholderBtn(CFG);

      // ✅ Pass sections so sticky bar can derive chip count dynamically
      ensureStickyBar(CFG, sections);

      let syncing = false, scheduled = false;

      const doSync = () => {
        if (syncing) return;
        syncing = true;
        try {
          steps.refreshDisabled();
          prefixChooseToSectionTitles(sections);
          ensureSectionCounters(sections);
          updateSectionCounters(sections);
          mountAddBtnIntoSlot(CFG);
          syncCtaPresentation(CFG);
          enhanceQtyWrappers();
          enhanceServingLabels(sections);
          syncStickyBar(CFG, sections);
        } finally {
          syncing = false;
        }
      };

      const scheduleSync = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; doSync(); });
      };

      doSync();

      new MutationObserver(scheduleSync).observe(ROOT, { childList: true, subtree: true });

      ROOT.addEventListener("click", (e) => {
        // ✅ FIX: Intercept native Recharge + button — block if section is already at/over limit.
        //    The custom + ADD button is guarded inside enhanceQtyWrappers, but the native
        //    .rb-btn-qty-plus is still directly clickable and must be stopped here.
        if (e.target?.closest(".rb-btn-qty-plus")) {
          const sec    = e.target.closest(".rb-section");
          const need   = sec ? sectionNeed(sec) : 0;
          const chosen = sec ? sectionChosen(sec) : 0;
          if (need > 0 && chosen >= need) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return; // hard cap — swallow the click entirely
          }
          scheduleSync();
          return;
        }
        if (e.target?.closest(".rb-btn-qty-minus")) scheduleSync();
      });

      ROOT.addEventListener("change", (e) => {
        if (e.target?.matches("input.rb-input, #selling-plan-list")) scheduleSync();
      });

      window.addEventListener("scroll", scheduleSync, { passive: true });
      window.addEventListener("resize", scheduleSync, { passive: true });
    });
  }

  boot();
})();